import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLearner } from '../hooks/useLearner';
import { useChildStories } from '../hooks/useChildStories';
import { useSeriesList, useSeriesDetail, useStartPrewrittenStory } from '../hooks/useLibrary';
import useFocusOnMount from '../hooks/useFocusOnMount';
import { canReview } from '../utils/stateHelpers';
import BottomNav from '../components/common/BottomNav';
import NavTabSelector from '../components/common/NavTabSelector';
import CloudBand from '../components/common/CloudBand';
import StoryCard from '../components/story/StoryCard';
import styles from './BookshelfPage.module.css';

export default function BookshelfPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: activeLearner } = useLearner();
  const mainRef = useFocusOnMount();
  // FP-CLIENT-SUBMIT-GUARD-01: synchronous single-fire guard for story-card nav.
  const storyNavRef = useRef(false);

  const FILTER_TABS = [
    { key: 'all', label: t('bookshelf.all') },
    { key: 'continue', label: t('bookshelf.continue') },
    { key: 'unreviewed', label: t('bookshelf.unreviewed') },
  ];

  const SECTION_TABS = [
    { key: 'daily', label: 'Daily Stories' },
    { key: 'ship', label: 'The Story Ship' },
  ];

  const [activeTab, setActiveTab] = useState('all');
  const [activeSection, setActiveSection] = useState('daily');
  const [selectedSeriesId, setSelectedSeriesId] = useState(null);

  const learnerId = activeLearner?.id;
  const { data: storiesData, isLoading: loading, error } = useChildStories(learnerId);
  const allStories = storiesData?.stories ?? [];

  // Story Ship data
  const { data: seriesList = [] } = useSeriesList(learnerId);
  const { data: seriesData, isLoading: shipLoading } = useSeriesDetail(
    selectedSeriesId,
    learnerId
  );
  const episodes = useMemo(() => seriesData?.episodes || [], [seriesData]);
  const startMutation = useStartPrewrittenStory();

  const carouselRef = useRef(null);
  const shipDragRef = useRef({ isDown: false, startX: 0, scrollLeft: 0, hasDragged: false, samples: [] });

  // Client-side tab filtering (same logic, derived from cached data)
  const stories = useMemo(() => {
    const statusMap = { continue: 'unread', unreviewed: 'read' };
    const statusFilter = statusMap[activeTab];
    if (!statusFilter) return allStories;
    return allStories.filter((s) => s.status === statusFilter);
  }, [allStories, activeTab]);

  // Initialise selectedSeriesId once the list loads
  useEffect(() => {
    if (seriesList.length > 0 && !selectedSeriesId) {
      setSelectedSeriesId(seriesList[0].id);
    }
  }, [seriesList, selectedSeriesId]);

  // Episode carousel — progressive scale + drag with momentum
  useEffect(() => {
    const track = carouselRef.current;
    if (!track || episodes.length === 0) return;

    function getMinScale() {
      const cardWidth = track.children[0]?.offsetWidth ?? 349;
      return cardWidth > 500 ? 0.902 : 0.86;
    }

    let scalePending = false;
    function updateScale() {
      const minScale = getMinScale();
      const trackCenter = track.scrollLeft + track.clientWidth / 2;
      Array.from(track.children).forEach((card) => {
        const cardCenter = card.offsetLeft + card.offsetWidth / 2;
        const distance = Math.abs(cardCenter - trackCenter);
        const cardWidth = card.offsetWidth;
        const normalized = Math.min(distance / cardWidth, 1);
        const eased = 1 - normalized * normalized;
        const scale = minScale + (1 - minScale) * eased;
        card.style.transform = `scale(${scale})`;
      });
    }
    function onScroll() {
      if (scalePending) return;
      scalePending = true;
      requestAnimationFrame(() => { updateScale(); scalePending = false; });
    }
    track.addEventListener('scroll', onScroll);
    updateScale();

    const drag = shipDragRef.current;
    drag.samples = [];

    function onMouseDown(e) {
      drag.isDown = true;
      drag.hasDragged = false;
      drag.startX = e.pageX;
      drag.scrollLeft = track.scrollLeft;
      drag.samples = [];
      track.style.cursor = 'grabbing';
      track.style.userSelect = 'none';
    }
    function onMouseMove(e) {
      if (!drag.isDown) return;
      e.preventDefault();
      const dx = drag.startX - e.pageX;
      if (Math.abs(dx) > 4) drag.hasDragged = true;
      track.scrollLeft = drag.scrollLeft + dx;
      drag.samples.push({ x: e.pageX, t: Date.now() });
      if (drag.samples.length > 5) drag.samples.shift();
    }
    function onMouseUp() {
      if (!drag.isDown) return;
      drag.isDown = false;
      track.style.cursor = '';
      track.style.userSelect = '';
      const samples = drag.samples;
      if (samples.length >= 2) {
        const oldest = samples[0];
        const newest = samples[samples.length - 1];
        const dt = newest.t - oldest.t;
        if (dt > 0 && dt < 250) {
          const vx = (newest.x - oldest.x) / dt;
          const throwDist = -vx * 350;
          track.scrollBy({ left: throwDist, behavior: 'smooth' });
        }
      }
      drag.samples = [];
    }
    function onMouseLeave() { if (drag.isDown) onMouseUp(); }

    track.addEventListener('mousedown', onMouseDown);
    track.addEventListener('mousemove', onMouseMove);
    track.addEventListener('mouseup', onMouseUp);
    track.addEventListener('mouseleave', onMouseLeave);

    return () => {
      track.removeEventListener('scroll', onScroll);
      track.removeEventListener('mousedown', onMouseDown);
      track.removeEventListener('mousemove', onMouseMove);
      track.removeEventListener('mouseup', onMouseUp);
      track.removeEventListener('mouseleave', onMouseLeave);
    };
  }, [episodes]);

  // Reset carousel scroll when series changes
  useEffect(() => {
    if (carouselRef.current) {
      carouselRef.current.scrollLeft = 0;
    }
  }, [selectedSeriesId]);

  function handleStoryClick(story) {
    // FP-CLIENT-SUBMIT-GUARD-01: single-fire the story-card entry (the Review
    // entry path included) so a slow transition can't be double-tapped into two
    // navigations. Page unmounts on navigate, so the latch resets naturally.
    if (storyNavRef.current) return;
    const storyId = story.id || story.story_id;
    storyNavRef.current = true;
    if (canReview(story)) {
      navigate(`/story/review/${storyId}`, { state: { story } });
    } else {
      navigate(`/story/read/${storyId}`);
    }
  }

  async function handleStartEpisode(episode) {
    if (episode.is_coming_soon) return;
    if (!episode.is_unlocked) return;
    if (episode.story_id) {
      navigate(`/story/read/${episode.story_id}`);
      return;
    }
    try {
      const result = await startMutation.mutateAsync({ templateId: episode.id, childId: learnerId });
      navigate(`/story/read/${result.storyId}`);
    } catch (err) {
      if (err?.response?.data?.error?.locked) {
        const { required_level, current_level } = err.response.data.error;
        alert(`You need to reach Level ${required_level} to read this episode. You are Level ${Math.max(1, current_level)}.`);
      } else {
        alert('Could not start this story. Please try again.');
      }
    }
  }

  const selectedSeries = seriesList.find((s) => s.id === selectedSeriesId) || null;

  return (
    <main
      className={`${styles.page}${activeSection === 'ship' ? ` ${styles.pageShip}` : ''}`}
      ref={mainRef}
      tabIndex={-1}
    >
      <CloudBand
        variant="cream"
        className={`${styles.cloudBand}${activeSection === 'ship' ? ` ${styles.cloudBandShip}` : ''}`}
      />

      {/* Story Ship balloon prop — only visible when ship tab active */}
      <img
        src="/assets/story-ship-bookshelf.png"
        alt=""
        aria-hidden="true"
        className={`${styles.balloonProp}${activeSection === 'ship' ? ` ${styles.balloonPropVisible}` : ''}`}
      />

      {/* ─── Content wrapper (constrained max-width) ─── */}
      <div className={styles.content}>

        {/* ─── Page header: title | section tabs | right controls ─── */}
        <div className={styles.pageHeader}>

          {/* Title — always "My Bookshelf" on mobile, dynamic on desktop */}
          <h1 className={styles.title}>
            <span className={`${styles.titleDefault}${activeSection === 'ship' ? ` ${styles.titleDefaultHidden}` : ''}`}>
              {t('bookshelf.title')}
            </span>
            <span className={`${styles.titleShip}${activeSection === 'ship' ? ` ${styles.titleShipVisible}` : ''}`}>
              The Story Ship
            </span>
          </h1>

          {/* Section tab switcher */}
          <NavTabSelector
            tabs={SECTION_TABS}
            active={activeSection}
            onChange={setActiveSection}
            className={styles.sectionTabWrapper}
          />

          {/* Right controls — filter tabs (Daily) or season dropdown (Ship) */}
          <div className={styles.rightControls}>
            {activeSection === 'daily' ? (
              <NavTabSelector
                tabs={FILTER_TABS}
                active={activeTab}
                onChange={setActiveTab}
                className={styles.filterTabWrap}
              />
            ) : (
              <div className={styles.seasonDropdownWrap}>
                <select
                  className={styles.seasonDropdownNative}
                  value={selectedSeriesId || ''}
                  onChange={(e) => setSelectedSeriesId(e.target.value)}
                  aria-label="Select season"
                >
                  {[...seriesList]
                    .sort((a, b) => (b.is_featured ? 1 : 0) - (a.is_featured ? 1 : 0))
                    .map((series) => (
                      <option key={series.id} value={series.id}>
                        {series.is_featured ? `★ ${series.name}` : series.name}
                      </option>
                    ))}
                </select>

                <div className={styles.seasonDropdownDisplay} aria-hidden="true">
                  <div className={styles.seasonDropdownLeft}>
                    {selectedSeries?.is_featured && (
                      <span className={styles.featuredStar} aria-hidden="true">★</span>
                    )}
                    <span className={styles.seasonDropdownLabel}>
                      {selectedSeries?.name || 'Season 1'}
                    </span>
                    {selectedSeries?.has_new_episode && (
                      <span className={styles.seasonNewBadge}>NEW EPISODE</span>
                    )}
                  </div>
                  <svg
                    className={styles.seasonChevron}
                    width="8"
                    height="4"
                    viewBox="0 0 8 4"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path d="M0 0L4 4L8 0" stroke="#211E1A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ─── Daily Stories content ─── */}
        {activeSection === 'daily' && (
          <div className={styles.storyList} aria-live="polite">
            {loading &&
              [0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i} className={`skeleton ${styles.skeletonCard}`} />
              ))}

            {error && !loading && (
              <div className={styles.errorCard} role="alert">
                <p className={styles.errorMessage}>
                  {error.message || t('bookshelf.loadError')}
                </p>
                <button type="button" onClick={() => navigate('/')} className={styles.actionBtn}>
                  {t('common.goHome')}
                </button>
              </div>
            )}

            {!loading && !error && stories.length === 0 && (
              <div className={styles.emptyState}>
                <img
                  src="/assets/bookshelf-empty.webp"
                  alt="Characters sitting on a stack of books"
                  className={styles.emptyIllustration}
                />
                <div className={styles.emptyTextBlock}>
                  <p className={styles.emptyTitle}>
                    {activeTab === 'all'
                      ? t('bookshelf.noStories')
                      : activeTab === 'continue'
                        ? t('bookshelf.nothingToContinue')
                        : t('bookshelf.allCaughtUp')}
                  </p>
                  <p className={styles.emptySubtitle}>
                    {activeTab === 'all'
                      ? t('bookshelf.headHome')
                      : t('bookshelf.checkBack')}
                  </p>
                </div>
                {activeTab === 'all' && (
                  <button type="button" onClick={() => navigate('/')} className={styles.actionBtn}>
                    {t('bookshelf.createStory')}
                  </button>
                )}
              </div>
            )}

            {!loading &&
              !error &&
              stories.map((story, i) => (
                <StoryCard
                  key={story.id || story.story_id || i}
                  story={story}
                  onClick={() => handleStoryClick(story)}
                />
              ))}
          </div>
        )}

        {/* ─── Story Ship — loading / empty states ─── */}
        {activeSection === 'ship' && shipLoading && (
          <div className={styles.shipLoadingState}>
            <div className={`skeleton ${styles.skeletonCard}`} style={{ height: 434 }} />
          </div>
        )}
        {activeSection === 'ship' && !shipLoading && episodes.length === 0 && (
          <div className={styles.emptyState}>
            <div className={styles.emptyTextBlock}>
              <p className={styles.emptyTitle}>Coming Soon</p>
              <p className={styles.emptySubtitle}>
                {seriesList.length === 0
                  ? 'No seasons available yet. Check back soon!'
                  : 'No episodes available in this season yet.'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* ─── Story Ship episode carousel — full viewport width ─── */}
      {activeSection === 'ship' && !shipLoading && episodes.length > 0 && (
        <div
          className={styles.episodeCarouselTrack}
          ref={carouselRef}
          role="list"
          aria-label="Episodes"
        >
          {episodes.map((episode) => {
            const isLocked = !episode.is_unlocked;
            const coverUrl = episode.image_url || seriesData?.series?.cover_image_url || null;
            return (
              <button
                key={episode.id}
                type="button"
                role="listitem"
                className={`${styles.episodeCard}${episode.is_coming_soon ? ' ' + styles.episodeCardComingSoon : ''}`}
                onClick={() => {
                  if (shipDragRef.current.hasDragged) return;
                  handleStartEpisode(episode);
                }}
                disabled={startMutation.isPending || episode.is_coming_soon}
                aria-label={
                  episode.is_coming_soon
                    ? `Episode ${episode.episode_number}: Coming Soon`
                    : isLocked
                    ? `Episode ${episode.episode_number} — locked, requires Level ${episode.required_level}`
                    : `Episode ${episode.episode_number}: ${episode.title}`
                }
              >
                {coverUrl ? (
                  <>
                    <div
                      className={styles.episodeCardBg}
                      style={{ backgroundImage: `url(${coverUrl})` }}
                    />
                    <div className={styles.episodeCardOverlay} />
                  </>
                ) : (
                  <div className={`${styles.episodeCardBg} ${styles.episodeCardBgFallback}`} />
                )}

                {isLocked && !episode.is_coming_soon && (
                  <div className={styles.episodeLockOverlay}>
                    <span className={styles.episodeLockIcon} aria-hidden="true">🔒</span>
                    <span className={styles.episodeLockLabel}>
                      Level {episode.required_level} required
                    </span>
                  </div>
                )}

                {/* Episode status badge — priority: coming_soon > completed > new */}
                {episode.is_coming_soon && (
                  <div className={styles.episodeBadge + ' ' + styles.episodeBadgeComingSoon}>
                    Coming Soon
                  </div>
                )}
                {!episode.is_coming_soon && episode.is_completed && (
                  <div className={styles.episodeBadge + ' ' + styles.episodeBadgeCompleted}>
                    Completed
                  </div>
                )}
                {!episode.is_coming_soon && !episode.is_completed && episode.is_new && (
                  <div className={styles.episodeBadge + ' ' + styles.episodeBadgeNew}>
                    New
                  </div>
                )}

                <div className={styles.episodeCardContent}>
                  <p className={styles.episodeCardNumber}>
                    Episode {episode.episode_number}
                  </p>
                  <p className={styles.episodeCardTitle}>{episode.title}</p>
                </div>
              </button>
            );
          })}
        </div>
      )}

      <BottomNav active="bookshelf" />
    </main>
  );
}
